import * as yup from 'yup';

import { CANADA, USA } from '../constants/countries';
import { MARKETPLACE_COUNTRY_ERROR } from '../constants/marketplace';
import {
  RECEIVER_ADDRESS1,
  RECEIVER_ADDRESS2,
  RECEIVER_CITY,
  RECEIVER_COMPANY_NAME,
  RECEIVER_COUNTRY,
  RECEIVER_EMAIL,
  RECEIVER_FIRST_NAME,
  RECEIVER_LAST_NAME,
  RECEIVER_PHONE,
  RECEIVER_STATE,
  RECEIVER_ZIP,
} from '../constants/shipping';
import { CUSTOMIZABLE_ITEM_TEXT_MAX_LENGTH } from '../constants/ui';
import { IAddressInfo, IContactInfo, IFixedAddress } from '../types/api';
import { ICustomizableViewItem } from '../types/inventory';
import { ITaxRequiredCountries } from '../types/shell';
import { IPickAddressForm, IShippingStepValidationFlags, TFullAddressForm } from '../types/shipping';
import { AddressChoiceEnum } from '../types/ui';
import { getTaxRequirementInfo } from './helpers';

const EMAIL_VALIDATION_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const ZIP_VALIDATION_REGEX = /^\s*\w[\w\s-]{2,10}\s*$/;

function getReceiverTaxNumberField({
  usePickedAddressId = false,
  useUserAddress = false,
  fallbackCountry,
  fixedAddresses,
  taxRequiredCountries,
}: {
  modeField?: string;
  usePickedAddressId?: boolean;
  useUserAddress?: boolean;
  fallbackCountry?: string;
  fixedAddresses?: IFixedAddress[];
  taxRequiredCountries?: ITaxRequiredCountries;
}) {
  return yup.string().test({
    name: 'receiver_tax_number',
    message: 'This field is required',
    test(this: yup.TestContext, value?: string) {
      const parent = this.parent;
      const currentMode = parent?.mode;

      const hasUserAddress = useUserAddress && parent?.userAddress?.receiver_country;
      const hasPickedAddress = usePickedAddressId && parent?.picked_address_id;
      const hasModeField = currentMode !== undefined;
      const isFormMode = currentMode === AddressChoiceEnum.Form;
      const isSelectMode = currentMode === AddressChoiceEnum.Select;

      // Check if we should validate based on mode restrictions
      const shouldValidateUserAddress = hasModeField ? isFormMode : true;
      const shouldValidatePickedAddress = hasModeField ? isSelectMode : true;

      // User address form validation
      if (hasUserAddress && shouldValidateUserAddress) {
        const country = parent.userAddress.receiver_country as keyof ITaxRequiredCountries;
        const isRequired = !!taxRequiredCountries?.[country];

        return !isRequired || !!value;
      }

      // Address selection validation
      if (hasPickedAddress && shouldValidatePickedAddress) {
        const pickedId = parent.picked_address_id;
        const { isRequired } = getTaxRequirementInfo(fixedAddresses, pickedId, taxRequiredCountries);

        return !isRequired || !!value;
      }

      // Fixed address preselected by send creator
      if (fallbackCountry) {
        const isRequired = !!taxRequiredCountries?.[fallbackCountry as keyof ITaxRequiredCountries];

        return !isRequired || !!value;
      }

      return true;
    },
  });
}

export const userAddressValidation = yup.object<IAddressInfo>().shape({
  [RECEIVER_ADDRESS1]: yup.string().required('Address 1 is required').trim(),
  [RECEIVER_ADDRESS2]: yup.string().trim(),
  [RECEIVER_CITY]: yup.string().required('City is required').trim(),
  [RECEIVER_COMPANY_NAME]: yup.string().trim(),
  [RECEIVER_COUNTRY]: yup.string().required('Country is required').trim(),
  [RECEIVER_STATE]: yup
    .string()
    .trim()
    .when(RECEIVER_COUNTRY, {
      is: (country: string) => country === USA.two_digit_code || country === CANADA.two_digit_code,
      then: (schema: yup.StringSchema) => schema.required('State is required'),
      otherwise: (schema: yup.StringSchema) => schema.notRequired(),
    }),
  [RECEIVER_ZIP]: yup
    .string()
    .matches(ZIP_VALIDATION_REGEX, { message: 'Invalid zip format' })
    .required('ZIP Code is required')
    .trim(),
});

export const contactValidation = (
  isDigital: boolean,
  taxRequiredCountries?: ITaxRequiredCountries,
  addresses?: IFixedAddress[],
) =>
  yup.object<IContactInfo>().shape({
    [RECEIVER_FIRST_NAME]: yup.string().required('First name is required').trim(),
    [RECEIVER_LAST_NAME]: yup.string().required('Last name is required').trim(),
    [RECEIVER_EMAIL]: yup
      .string()
      .matches(EMAIL_VALIDATION_REGEX, 'Invalid email format')
      .required('Email is required')
      .trim(),
    [RECEIVER_PHONE]: yup
      .string()
      .trim()
      .when([], {
        is: () => !isDigital,
        then: (schema) => schema.required('Phone is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    receiver_tax_number: getReceiverTaxNumberField({
      // Only a single fixed address is never picked; with several, the pick-aware schema decides.
      fallbackCountry: addresses?.length === 1 ? addresses[0].country : undefined,
      taxRequiredCountries,
    }),
  });

const pickedAddressCountryTest = (fixedAddresses?: IFixedAddress[], allowedCountries?: string[]) => ({
  name: 'picked_address_country',
  message: MARKETPLACE_COUNTRY_ERROR,
  test: (value?: string) => {
    if (!allowedCountries?.length || !value) {
      return true;
    }
    const picked = fixedAddresses?.find((address) => address.uid === value);

    return !picked || allowedCountries.includes(picked.country);
  },
});

const fixedAddressChoiceValidation = (
  taxRequiredCountries?: ITaxRequiredCountries,
  fixedAddresses?: IFixedAddress[],
  allowedCountries?: string[],
) =>
  yup.object<IPickAddressForm>().shape({
    picked_address_id: yup
      .string()
      .required('Address is required')
      .test(pickedAddressCountryTest(fixedAddresses, allowedCountries)),
    receiver_tax_number: getReceiverTaxNumberField({
      usePickedAddressId: true,
      fixedAddresses,
      taxRequiredCountries,
    }),
  });

const getUserAddressValidation = (allowedCountries?: string[]) =>
  allowedCountries?.length
    ? userAddressValidation.shape({
        [RECEIVER_COUNTRY]: yup
          .string()
          .required('Country is required')
          .oneOf(allowedCountries, MARKETPLACE_COUNTRY_ERROR)
          .trim(),
      })
    : userAddressValidation;

export const addressValidation = (
  taxRequiredCountries?: ITaxRequiredCountries,
  fixedAddresses?: IFixedAddress[],
  allowedCountries?: string[],
) =>
  yup.object<TFullAddressForm>().shape({
    mode: yup.mixed().oneOf(Object.values(AddressChoiceEnum)).required('Delivery Address is required'),
    picked_address_id: yup.string().when('mode', {
      is: AddressChoiceEnum.Select,
      then: (sch: yup.StringSchema) =>
        sch.required('Address is required').test(pickedAddressCountryTest(fixedAddresses, allowedCountries)),
    }),
    userAddress: yup.object().when('mode', {
      is: AddressChoiceEnum.Form,
      then: (sch) => sch.concat(getUserAddressValidation(allowedCountries)),
      otherwise: () => yup.object(),
    }),
    receiver_tax_number: getReceiverTaxNumberField({
      usePickedAddressId: true,
      useUserAddress: true,
      fixedAddresses,
      taxRequiredCountries,
    }),
  });

const nestedAddressSchema = (taxRequiredCountries?: ITaxRequiredCountries, allowedCountries?: string[]) =>
  yup.object().shape({
    userAddress: getUserAddressValidation(allowedCountries).required(),
    receiver_tax_number: getReceiverTaxNumberField({
      useUserAddress: true,
      taxRequiredCountries,
    }),
  });

export const getShippingStepValidation = ({
  isDigital,
  isFixed,
  numberOfOptions,
  taxRequiredCountries,
  fixedAddresses,
  addresses,
  allowedCountries,
}: IShippingStepValidationFlags): yup.AnyObjectSchema => {
  let schema: yup.AnyObjectSchema = contactValidation(isDigital, taxRequiredCountries, fixedAddresses);

  switch (true) {
    case isDigital: {
      break;
    }
    case isFixed && numberOfOptions > 1: {
      schema = schema.concat(fixedAddressChoiceValidation(taxRequiredCountries, fixedAddresses, allowedCountries));
      break;
    }
    case !isFixed && !!numberOfOptions: {
      schema = schema.concat(addressValidation(taxRequiredCountries, addresses, allowedCountries));
      break;
    }
    case !isFixed && !numberOfOptions: {
      schema = schema.concat(nestedAddressSchema(taxRequiredCountries, allowedCountries));
      break;
    }
    default:
      break;
  }

  return schema;
};

export const customizedTextsValidation = (items: ICustomizableViewItem[]) => {
  return yup.object<{ [k in keyof ICustomizableViewItem]: string }>().shape(
    (items || []).length
      ? items.reduce((acc, item) => {
          const { internalId, item_customization_required } = item || {};
          if (internalId) {
            const commonSchema = yup
              .string()
              .max(CUSTOMIZABLE_ITEM_TEXT_MAX_LENGTH, ({ max }) => `Maximum length is ${max} characters`)
              .trim();

            return {
              ...acc,
              [internalId]: item_customization_required
                ? commonSchema.required('This field is required')
                : commonSchema.notRequired(),
            };
          }

          return { ...acc };
        }, {} as { [k in keyof ICustomizableViewItem]: yup.StringSchema })
      : {},
  );
};

export const isPhoneNumber = (inputValue: string) => {
  // start validating only after 5 first chars
  if (inputValue.length < 5) {
    return false;
  }

  // Ensure input starts with a digit or "+" and has additional digits after
  return /^\+?[0-9]\d{1,14}$/.test(inputValue);
};

export const trimmedValue = (value: string) => value?.replace(/^,|,$/g, '').trim();

export const isValidEmail = (email: string) => new RegExp(EMAIL_VALIDATION_REGEX).test(trimmedValue(email));
